import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Renders and drives the store-wide "Bundle & Save" purchase-option module.
 *
 * Never submits or duplicates the product form itself -- it only updates the existing
 * product-form-component's hidden variant-id input (ref="variantId") and quantity
 * selector, exactly the way bulk-discount-tiers.js already does for quantity tiers.
 * The existing Add to Cart button, cart drawer refresh, and checkout flow are reused
 * completely unchanged.
 *
 * Supports three Bundle variant-matching modes (see snippets/bundle-and-save.liquid):
 *  - Case A: Bundle has a single variant -- used directly, no matching needed.
 *  - Case B: Bundle's option structure matches the base product's -- the correct
 *    variant is looked up automatically whenever the base product's own variant
 *    changes (Bundle has no visible selector of its own).
 *  - Case C: Bundle has its own independent variants -- its own <select> elements
 *    (rendered inside the selected card) drive the lookup.
 *
 * @extends {Component}
 */
class BundleAndSaveComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Record<string, any>} */
  #bundleData = {};

  /** @type {string} */
  #currentBaseOptionsKey = '';

  /** @type {{min: string, max: string | null, step: string} | undefined} */
  #baseQuantityConstraints;

  /** @type {boolean | undefined} */
  #acceleratedCheckoutWasHidden;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#parseBundleData();
    this.#currentBaseOptionsKey = this.dataset.baseVariantOptionsKey || '';

    this.addEventListener('change', this.#onRadioChange, { signal });
    this.addEventListener('change', this.#onVariantSelectChange, { signal });

    const section = this.closest('.shopify-section, dialog, product-card');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onBaseVariantUpdate, { signal });

    this.#captureBaseQuantityConstraints();
    this.#refreshMatchStructureAvailability();

    const checkedInput = this.querySelector('[data-bundle-radio]:checked');
    if (checkedInput instanceof HTMLInputElement) {
      this.#applySelection(checkedInput.value, { initial: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #parseBundleData() {
    const scripts = this.querySelectorAll('script[data-bundle-json]');
    scripts.forEach((script) => {
      try {
        const data = JSON.parse(script.textContent || '{}');
        if (data.productId) this.#bundleData[data.productId] = data;
      } catch (error) {
        console.error('Bundle & Save: failed to parse bundle data', error);
      }
    });
  }

  /** @returns {any | undefined} */
  #getProductForm() {
    return /** @type {any | null} */ (this.closest('product-form-component'));
  }

  #captureBaseQuantityConstraints() {
    const quantitySelector = this.#getProductForm()?.refs?.quantitySelector;
    const input = quantitySelector?.refs?.quantityInput;
    if (!input) return;

    this.#baseQuantityConstraints = {
      min: input.min || '1',
      max: input.max || null,
      step: input.step || '1',
    };
  }

  /** @param {Event} event */
  #onRadioChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-bundle-radio]')) return;

    this.#applySelection(input.value, { initial: false });
  };

  /** @param {Event} event */
  #onVariantSelectChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-bundle-variant-select]')) return;

    const card = select.closest('[data-bundle-option]');
    const productId = card?.getAttribute('data-product-id');
    if (!productId) return;

    this.#resolveIndependentBundle(productId);

    const checkedInput = this.querySelector('[data-bundle-radio]:checked');
    if (checkedInput instanceof HTMLInputElement && checkedInput.value === productId) {
      this.#applySelection(productId, { initial: false });
    }
  };

  /**
   * Re-evaluates every Case B (matches-base-structure) Bundle card's availability
   * against the current base product option-value combination, without changing
   * the active selection. Called on connect and whenever the base variant changes.
   */
  #refreshMatchStructureAvailability() {
    const cards = this.querySelectorAll('[data-bundle-option][data-matches-base="true"]');
    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const productId = card.getAttribute('data-product-id');
      if (!productId) return;
      this.#resolveMatchedBundle(productId);
    });
  }

  /**
   * Finds the variant matching the current base option-value combination for a
   * Case B bundle, updates that card's displayed data + availability, and returns it.
   * @param {string} productId
   * @returns {any | null}
   */
  #resolveMatchedBundle(productId) {
    const data = this.#bundleData[productId];
    if (!data) return null;

    const variant = data.variants.find(
      /** @param {any} v */ (v) => v.options.join('@@') === this.#currentBaseOptionsKey
    );

    this.#updateCardState(productId, variant || null);
    return variant || null;
  }

  /**
   * Finds the variant matching a Case C bundle's own <select> values, updates that
   * card's displayed data + availability, and returns it.
   * @param {string} productId
   * @returns {any | null}
   */
  #resolveIndependentBundle(productId) {
    const data = this.#bundleData[productId];
    if (!data) return null;

    const card = this.querySelector(`[data-bundle-option][data-product-id="${CSS.escape(productId)}"]`);
    const selects = card?.querySelectorAll('[data-bundle-variant-select]');
    if (!selects || selects.length === 0) return null;

    const targetValues = Array.from(selects)
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((select) => /** @type {HTMLSelectElement} */ (select).value);

    const variant = data.variants.find(/** @param {any} v */ (v) => v.options.join('@@') === targetValues.join('@@'));

    this.#updateCardState(productId, variant || null);
    return variant || null;
  }

  /**
   * @param {string} productId
   * @returns {any | null} The variant to use for this bundle: its only variant (Case A),
   * or the result of the appropriate matching strategy (Case B/C).
   */
  #resolveBundleVariant(productId) {
    const data = this.#bundleData[productId];
    if (!data) return null;

    if (data.variants.length === 1) {
      const variant = data.variants[0];
      this.#updateCardState(productId, variant);
      return variant;
    }

    if (data.isIndependent) return this.#resolveIndependentBundle(productId);
    if (data.matchesBaseStructure) return this.#resolveMatchedBundle(productId);

    // Fallback: structure unknown/unmatched -- use the product's own default variant.
    const fallback = data.variants.find(/** @param {any} v */ (v) => v.available) || data.variants[0] || null;
    this.#updateCardState(productId, fallback);
    return fallback;
  }

  /**
   * Updates a Bundle card's price/compare/savings/unit-price/availability display
   * for the given resolved variant (or null when no variant matches).
   * @param {string} productId
   * @param {any | null} variant
   */
  #updateCardState(productId, variant) {
    const card = this.querySelector(`[data-bundle-option][data-product-id="${CSS.escape(productId)}"]`);
    if (!(card instanceof HTMLElement)) return;

    const radio = card.querySelector('[data-bundle-radio]');
    const priceEl = card.querySelector('[data-bundle-price]');
    const compareEl = card.querySelector('[data-bundle-compare]');
    const savingsEl = card.querySelector('[data-bundle-savings]');
    const unitPriceEl = card.querySelector('[data-bundle-unit-price]');
    const unavailableEl = card.querySelector('[data-bundle-unavailable-text]');

    const available = !!variant && variant.available;
    card.classList.toggle('is-unavailable', !available);
    card.setAttribute('data-available', String(available));
    if (variant) card.setAttribute('data-variant-id', variant.id);

    if (radio instanceof HTMLInputElement) {
      radio.disabled = !available;
      if (!available && radio.checked) radio.checked = false;
    }

    if (priceEl instanceof HTMLElement) {
      if (variant?.price) {
        priceEl.textContent = variant.price;
        priceEl.hidden = false;
      } else {
        priceEl.hidden = true;
      }
    }

    if (compareEl instanceof HTMLElement) {
      if (variant?.showCompare) {
        compareEl.textContent = variant.compare;
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
        compareEl.textContent = '';
      }
    }

    if (savingsEl instanceof HTMLElement) {
      if (variant?.showSavings) {
        savingsEl.textContent = `Save ${variant.savingsAmount} (${variant.savingsPercent}%)`;
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
        savingsEl.textContent = '';
      }
    }

    if (unitPriceEl instanceof HTMLElement) {
      if (variant?.unitPrice) {
        const unitLabel = unitPriceEl.dataset.unitLabel || unitPriceEl.textContent?.split('/')[1] || '';
        unitPriceEl.textContent = unitLabel ? `${variant.unitPrice}/${unitLabel}` : variant.unitPrice;
        unitPriceEl.hidden = false;
      } else {
        unitPriceEl.hidden = true;
      }
    }

    if (unavailableEl instanceof HTMLElement) {
      unavailableEl.hidden = available;
    }
  }

  /**
   * Applies a purchase-option selection: "single" or a Bundle product id.
   * Updates the product form's hidden variant id, quantity constraints, Add to Cart
   * availability, and the accelerated checkout (dynamic checkout) button visibility.
   * @param {string} value
   * @param {{initial: boolean}} options
   */
  #applySelection(value, { initial }) {
    this.#setSelectedVisualState(value);

    const productForm = this.#getProductForm();
    if (!productForm) return;

    const { variantId, quantitySelector, addToCartButtonContainer, acceleratedCheckoutButtonContainer } =
      productForm.refs;

    if (this.#acceleratedCheckoutWasHidden === undefined && acceleratedCheckoutButtonContainer) {
      this.#acceleratedCheckoutWasHidden = acceleratedCheckoutButtonContainer.hasAttribute('hidden');
    }

    if (value === 'single') {
      const singleVariantId = this.dataset.singleVariantId;
      if (variantId && singleVariantId) variantId.value = singleVariantId;

      if (this.#baseQuantityConstraints && quantitySelector?.updateConstraints) {
        quantitySelector.updateConstraints(
          this.#baseQuantityConstraints.min,
          this.#baseQuantityConstraints.max,
          this.#baseQuantityConstraints.step
        );
      }

      const singleOption = this.querySelector('[data-option-type="single"]');
      const singleAvailable = singleOption?.getAttribute('data-available') === 'true';
      if (singleAvailable) addToCartButtonContainer?.enable?.();
      else addToCartButtonContainer?.disable?.();

      if (acceleratedCheckoutButtonContainer) {
        if (!this.#acceleratedCheckoutWasHidden) acceleratedCheckoutButtonContainer.removeAttribute('hidden');
      }

      return;
    }

    // Bundle selected: hide dynamic checkout -- Shopify's payment_button is bound to
    // this form's original product/variant at render time and is not reliable for a
    // different (Bundle) product's variant without invasive changes.
    acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');

    const variant = this.#resolveBundleVariant(value);

    if (!variant) {
      addToCartButtonContainer?.disable?.();
      if (!initial) this.#announceUnavailable();
      return;
    }

    if (variantId) variantId.value = variant.id;

    if (quantitySelector?.updateConstraints) {
      quantitySelector.updateConstraints(
        String(variant.quantityMin || 1),
        variant.quantityMax != null ? String(variant.quantityMax) : null,
        String(variant.quantityStep || 1)
      );
    }

    if (variant.available) addToCartButtonContainer?.enable?.();
    else addToCartButtonContainer?.disable?.();
  }

  #announceUnavailable() {
    const productForm = this.#getProductForm();
    const liveRegion = productForm?.refs?.liveRegion;
    if (liveRegion) liveRegion.textContent = 'This bundle option is currently unavailable.';
  }

  /**
   * Updates the checked/visual state across all option cards, and shows/hides the
   * independent variant-picker for whichever Bundle card is currently selected.
   * @param {string} value
   */
  #setSelectedVisualState(value) {
    const options = this.querySelectorAll('[data-bundle-option]');
    options.forEach((option) => {
      if (!(option instanceof HTMLElement)) return;

      const isSingle = option.dataset.optionType === 'single';
      const optionValue = isSingle ? 'single' : option.dataset.productId;
      const isSelected = optionValue === value;

      const radio = option.querySelector('[data-bundle-radio]');
      if (radio instanceof HTMLInputElement) radio.checked = isSelected;

      const picker = option.querySelector('[data-bundle-variant-picker]');
      if (picker instanceof HTMLElement) picker.hidden = !isSelected;
    });
  }

  /**
   * Keeps Case B Bundle cards in sync with the base product's own variant changes,
   * including re-selecting the correct Bundle variant if that Bundle option is
   * currently selected, and falling back to Single if the new base combination has
   * no matching Bundle variant.
   * @param {import('./events').VariantUpdateEvent} event
   */
  #onBaseVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.closest('product-form-component')?.dataset.productId) return;
    if (!event.detail.resource) return;

    this.#currentBaseOptionsKey = (event.detail.resource.options || []).join('@@');
    this.#refreshMatchStructureAvailability();
    this.#syncSingleCardFromVariantUpdate(event);

    const checkedInput = this.querySelector('[data-bundle-radio]:checked');
    const selectedValue = checkedInput instanceof HTMLInputElement ? checkedInput.value : 'single';

    if (selectedValue === 'single') return;

    const data = this.#bundleData[selectedValue];
    if (!data?.matchesBaseStructure) return;

    const variant = this.#resolveMatchedBundle(selectedValue);
    const productForm = this.#getProductForm();

    if (!variant) {
      // No longer available in this base combination -- fall back to Single safely.
      this.#applySelection('single', { initial: false });
      return;
    }

    if (productForm?.refs?.variantId) productForm.refs.variantId.value = variant.id;
    if (variant.available) productForm?.refs?.addToCartButtonContainer?.enable?.();
    else productForm?.refs?.addToCartButtonContainer?.disable?.();
  };

  /**
   * Keeps the "Single item" card's displayed price/availability and cached quantity
   * constraints in sync with the base product's own variant changes, reusing the
   * already-fetched server-rendered section HTML (same source Horizon's own product
   * form uses) instead of formatting money in JavaScript.
   * @param {import('./events').VariantUpdateEvent} event
   */
  #syncSingleCardFromVariantUpdate(event) {
    const resource = event.detail.resource;
    if (!resource) return;

    this.dataset.singleVariantId = resource.id;

    const html = event.detail.data.html;
    const newSingleCard = html?.querySelector?.('[data-option-type="single"]');
    const currentSingleCard = this.querySelector('[data-option-type="single"]');

    if (newSingleCard && currentSingleCard instanceof HTMLElement) {
      const newPrice = newSingleCard.querySelector('.paw-bundle-option__price')?.textContent;
      const priceEl = currentSingleCard.querySelector('.paw-bundle-option__price');
      if (newPrice && priceEl) priceEl.textContent = newPrice;

      const newAvailable = newSingleCard.getAttribute('data-available') === 'true';
      currentSingleCard.setAttribute('data-available', String(newAvailable));
      currentSingleCard.classList.toggle('is-unavailable', !newAvailable);

      const radio = currentSingleCard.querySelector('[data-bundle-radio]');
      if (radio instanceof HTMLInputElement) radio.disabled = !newAvailable;
    }

    const newQuantityInput = html?.querySelector?.('quantity-selector-component input[ref="quantityInput"]');
    if (newQuantityInput instanceof HTMLInputElement) {
      this.#baseQuantityConstraints = {
        min: newQuantityInput.min || '1',
        max: newQuantityInput.max || null,
        step: newQuantityInput.step || '1',
      };
    }
  }
}

if (!customElements.get('bundle-and-save-component')) {
  customElements.define('bundle-and-save-component', BundleAndSaveComponent);
}
